import React, { useEffect, useRef } from 'react';
import './FloatingFormWindow.css';

/**
 * FloatingFormWindow - A hovering form panel similar to FAB style
 * Appears in bottom-right area, can be dragged, minimized, closed
 *
 * @param {boolean} show - Whether to show the window
 * @param {function} onHide - Callback when window is closed
 * @param {string} title - Window title
 * @param {ReactNode} children - Form content
 * @param {string} icon - Icon class (e.g., 'fa-tasks')
 */
export default function FloatingFormWindow({
  show,
  onHide,
  title,
  children,
  icon = 'fa-edit'
}) {
  const windowRef = useRef(null);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState({ bottom: 100, right: 30 });
  const dragStartPos = useRef({ x: 0, y: 0, bottom: 0, right: 0 });

  useEffect(() => {
    if (!show) {
      setIsMinimized(false);
    }
  }, [show]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.floating-window-actions')) return;
    if (e.target.closest('.floating-window-body')) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      bottom: position.bottom,
      right: position.right
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaX = dragStartPos.current.x - e.clientX;
      const deltaY = dragStartPos.current.y - e.clientY;

      setPosition({
        bottom: dragStartPos.current.bottom + deltaY,
        right: dragStartPos.current.right + deltaX
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!show) return null;

  return (
    <div
      ref={windowRef}
      className={`floating-form-window ${isMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        bottom: `${position.bottom}px`,
        right: `${position.right}px`
      }}
    >
      <div
        className="floating-window-header"
        onMouseDown={handleMouseDown}
      >
        <div className="floating-window-title">
          <i className={`fas ${icon} me-2`}></i>
          {title}
        </div>
        <div className="floating-window-actions">
          <button
            className="floating-window-btn"
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <i className={`fas ${isMinimized ? 'fa-window-maximize' : 'fa-window-minimize'}`}></i>
          </button>
          <button
            className="floating-window-btn close"
            onClick={onHide}
            title="Close"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="floating-window-body">
          {children}
        </div>
      )}
    </div>
  );
}
